/**
 * PaymentPasswordModal — 通用「支付密码」输入弹层（资金 2FA，可复用）
 *
 * 用于订单支付等资金操作前的安全校验：
 *   - 6 位数字输入，本地即时校验；
 *   - 父级通过 error / showSetupHint 呈现后端 2004/2006/2007 差异化引导
 *     （未设置 → 去设置；错误 → 重试；锁定 → 联系平台）；
 *   - 提交携带支付密码，由父级调用真实支付接口。
 *
 * 复用全局 modal/form/btn 样式；仅 .ppwd-* 由本组件配套 CSS 提供。
 */
import { useEffect, useState } from 'react';
import { validatePayPassword } from '@/utils/paymentPassword';
import '@/styles/components/payment-password.css';

export interface PaymentPasswordModalProps {
  /** 是否展示 */
  open: boolean;
  /** 弹层标题，默认「安全校验」 */
  title?: string;
  /** 操作说明（如订单号 / 金额） */
  desc?: string;
  /** 提交中（禁用输入与按钮） */
  submitting?: boolean;
  /** 后端返回的错误文案（2006 重试 / 2007 锁定等） */
  error?: string;
  /** 是否展示「未设置 → 去设置」引导（错误码 2004） */
  showSetupHint?: boolean;
  onCancel: () => void;
  /** 校验通过后回调（携带支付密码） */
  onSubmit: (password: string) => void;
  /** 「去设置」回调（可跳转个人中心支付密码设置） */
  onGoSetup?: () => void;
}

export default function PaymentPasswordModal({
  open,
  title = '安全校验',
  desc,
  submitting = false,
  error = '',
  showSetupHint = false,
  onCancel,
  onSubmit,
  onGoSetup,
}: PaymentPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');

  // 每次打开重置输入与本地校验错误
  useEffect(() => {
    if (open) {
      setPassword('');
      setLocalError('');
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    const msg = validatePayPassword(password);
    if (msg) {
      setLocalError(msg);
      return;
    }
    setLocalError('');
    onSubmit(password);
  };

  const shownError = localError || error;

  return (
    <div className="modal-mask" onClick={() => !submitting && onCancel()}>
      <div className="modal-panel ppwd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={() => !submitting && onCancel()} aria-label="关闭">×</button>
        </div>
        <div className="modal-body">
          <span className="ppwd-secure">🔒 资金安全校验</span>
          {desc ? <div className="ppwd-desc">{desc}</div> : null}
          <div className="form-group">
            <label className="form-label">
              <span className="req">*</span>支付密码
            </label>
            <input
              className="form-control ppwd-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              placeholder="请输入 6 位数字支付密码"
              value={password}
              disabled={submitting}
              onChange={(e) => {
                setPassword(e.target.value.replace(/\D/g, ''));
                setLocalError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitting) handleConfirm();
              }}
            />
            <div className="form-help">支付密码为 6 位数字，用于订单支付等资金操作的安全校验</div>
          </div>

          {shownError ? <div className="form-error">{shownError}</div> : null}

          {showSetupHint ? (
            <div className="ppwd-setup-hint">
              <span>您尚未设置支付密码。请先前往「个人中心 → 支付密码」完成设置，再回来支付。</span>
              {onGoSetup ? (
                <button className="btn btn-primary btn-sm" onClick={onGoSetup} type="button">
                  去设置支付密码
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" disabled={submitting} onClick={onCancel} type="button">
            取消
          </button>
          <button className="btn btn-primary" disabled={submitting} onClick={handleConfirm} type="button">
            {submitting ? '支付中…' : '确认支付'}
          </button>
        </div>
      </div>
    </div>
  );
}
